import React, { useState, useEffect, useCallback } from 'react';
import {
  listSalesEstimateBaskets,
  createSalesEstimateBasket,
  updateSalesEstimateBasket,
  deleteSalesEstimateBasket,
  getSalesEstimateBasket
} from '../../services/salesEstimateService';
import 'bootstrap/dist/css/bootstrap.min.css';

const COUNTRIES = ['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'];

function SalesEstimateBasketManager() {
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAsins, setNewAsins] = useState('');
  const [newCountry, setNewCountry] = useState('US');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAsins, setEditAsins] = useState('');
  const [editCountry, setEditCountry] = useState('US');
  const accountId = localStorage.getItem('selectedAccountValue') || '';

  const loadBaskets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listSalesEstimateBaskets(accountId);
      setBaskets(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadBaskets(); }, [loadBaskets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const asins = newAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (!newName || asins.length === 0) return;
    setLoading(true);
    setError('');
    try {
      await createSalesEstimateBasket({ name: newName, asins, country: newCountry, account_id: accountId });
      setNewName(''); setNewAsins(''); setShowNew(false);
      loadBaskets();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async (basket) => {
    setEditing(basket);
    setEditName(basket.name || '');
    setEditCountry(basket.country || 'US');
    setEditAsins((Array.isArray(basket.asins) ? basket.asins : []).join('\n'));
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const asins = editAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    setLoading(true);
    setError('');
    try {
      await updateSalesEstimateBasket(editing.id, { name: editName, asins, country: editCountry });
      setShowEdit(false); setEditing(null);
      loadBaskets();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este basket?')) return;
    setLoading(true);
    setError('');
    try {
      await deleteSalesEstimateBasket(id);
      loadBaskets();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-3">Gestión de Baskets de Sales Estimate</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <i className="bi bi-plus-lg me-1"></i> Nuevo Basket
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Nombre</th>
              <th>ASINs</th>
              <th>País</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {baskets.map(b => (
              <tr key={b.id}>
                <td>{b.name}</td>
                <td><span className="badge bg-secondary">{b.asins?.length || 0}</span></td>
                <td>{b.country}</td>
                <td>
                  <button className="btn btn-sm btn-outline-info me-2" onClick={() => openEdit(b)}>
                    <i className="bi bi-pencil-square"></i>
                  </button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(b.id)}>
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
            {baskets.length === 0 && (
              <tr><td colSpan={4} className="text-center text-muted">No hay baskets registrados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Nuevo Basket */}
      {showNew && (
        <div className="card mb-4 border-primary">
          <div className="card-body">
            <h6>Nuevo Basket</h6>
            <form onSubmit={handleCreate}>
              <div className="row g-2">
                <div className="col-md-3">
                  <input className="form-control form-control-sm" placeholder="Nombre" value={newName} onChange={e => setNewName(e.target.value)} required />
                </div>
                <div className="col-md-5">
                  <textarea className="form-control form-control-sm" rows={2} placeholder="ASINs (uno por línea o separados por coma)" value={newAsins} onChange={e => setNewAsins(e.target.value)} required />
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={newCountry} onChange={e => setNewCountry(e.target.value)}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2 d-flex gap-1">
                  <button className="btn btn-primary btn-sm" type="submit">Crear</button>
                  <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowNew(false)}>Cancelar</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Basket */}
      {showEdit && (
        <div className="card mb-4 border-info">
          <div className="card-body">
            <h6>Editar Basket</h6>
            <form onSubmit={handleEdit}>
              <div className="row g-2">
                <div className="col-md-3">
                  <input className="form-control form-control-sm" placeholder="Nombre" value={editName} onChange={e => setEditName(e.target.value)} required />
                </div>
                <div className="col-md-5">
                  <textarea className="form-control form-control-sm" rows={2} placeholder="ASINs (uno por línea o separados por coma)" value={editAsins} onChange={e => setEditAsins(e.target.value)} required />
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={editCountry} onChange={e => setEditCountry(e.target.value)}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2 d-flex gap-1">
                  <button className="btn btn-info btn-sm" type="submit">Guardar</button>
                  <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => { setShowEdit(false); setEditing(null); }}>Cancelar</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesEstimateBasketManager;
